// client/src/pages/auth/ForgotPassword.js
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../services/authService.js';
import Input from '../../components/ui/Input.js';
import Button from '../../components/ui/Button.js';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: verify code, 3: new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1: Send reset code
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authService.forgotPassword(email);
      setStep(2);
    } catch (err) {
      setError(err.data?.error || 'Failed to send reset code');
    }
    setLoading(false);
  };

  // Step 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authService.verifyReset(email, code);
      setStep(3);
    } catch (err) {
      setError(err.data?.error || 'Invalid or expired code');
    }
    setLoading(false);
  };

  // Step 3: Set new password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await authService.resetPassword(email, code, password);
      // Success - redirect to login with message
      navigate('/login', { state: { message: 'Password reset successfully. Please log in.' } });
    } catch (err) {
      setError(err.data?.error || 'Failed to reset password');
    }
    setLoading(false);
  };

  // Resend code
  const handleResend = async () => {
    setLoading(true);
    setError('');
    try {
      await authService.forgotPassword(email);
      setError(''); // Clear any previous errors
    } catch (err) {
      setError(err.data?.error || 'Failed to resend code');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-md mx-auto px-4 py-12">
        <h1 className="font-display text-3xl text-gold-dark dark:text-gold text-center mb-2">Reset Password</h1>
        <p className="text-royal-500 dark:text-royal-200 text-center mb-6">Step {step} of 3</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-600 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-xl p-6">
          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={handleEmailSubmit}>
              <p className="text-royal-100 mb-4 text-sm">
                Enter your email address and we'll send you a code to reset your password.
              </p>
              <Input
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>
            </form>
          )}

          {/* Step 2: Enter Code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode}>
              <p className="text-royal-100 mb-3 text-sm">
                We've sent a 6-digit code to <strong className="text-white">{email}</strong> to verify your identity.
              </p>
              <div className="bg-royal-800/60 border border-royal-600/50 rounded-lg p-3 mb-4">
                <p className="text-royal-200 text-xs leading-relaxed">
                  <span className="text-gold font-semibold">Don't see it?</span> Check your spam or junk folder — reset emails can sometimes end up there. If you still don't see it, tap "Resend code" below.
                </p>
              </div>
              <Input
                label="Verification Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                placeholder="000000"
                className="text-center text-2xl tracking-widest"
                required
              />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              <button
                type="button"
                onClick={handleResend}
                disabled={loading}
                className="block w-full text-center mt-3 text-royal-100 hover:text-gold text-sm disabled:opacity-50"
              >
                Resend code
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword}>
              <p className="text-royal-100 mb-4 text-sm">
                Enter your new password.
              </p>
              <Input
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <Input
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>

        <p className="text-center mt-6 text-royal-500 dark:text-royal-200 text-sm">
          Remember your password?{' '}
          <Link to="/login" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light">
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
}